import React, { useEffect, useState } from "react";
import Loader from "../components/Loader";
import Calling from "../api/Calling";
import { toast } from "react-toastify";
import { FaRegUser, FaChartLine, FaGamepad } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import MainButton from "../components/ul/MainButton";
import PlayAnalyticsChart from "../components/charts/PlayAnalyticsChart";
import UserChart from "../components/charts/UserChart";
import { IoMdAnalytics } from "react-icons/io";
import WithdrawAnalyticsChart from "../components/charts/WithdrawChart";
import getISTDate from "../components/getISTDate";
import toIST from "../components/toIST";

const dateRanges = [
  "today",
  "yesterday",
  "thisWeek",
  "thisMonth",
  "last6Months",
  "thisYear",
];

const Home = () => {
  const [loader, setLoader] = useState(false);
  const [userData, setUserData] = useState([]);
  const [playData, setPlayData] = useState([]);
  const [startDate, setStartDate] = useState(getISTDate());
  const [endDate, setEndDate] = useState(getISTDate());
  const [selectedBtn, setSelectedBtn] = useState("today");
  const [filteredGame, setFilteredGame] = useState("");
  const [filteredUser, setFilteredUser] = useState("");
  const [pendingWithdraw, setPendingWithdraw] = useState(0);
  const [withdrawData, setWithdrawData] = useState([]);

  const [type, setType] = useState("user");
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoader(true);
    try {
      const response = await Calling.AdminDashbord(startDate, endDate, type);
      if (response?.status) {
        setUserData(response?.totalUser || []);
        setPlayData(response?.totalPlay || []);
        setWithdrawData(response?.totalWithdraw || []);
        setPendingWithdraw(response?.totalPendingWithdraw || 0);
      } else {
        toast.error(response?.message || "Failed to fetch dashboard data.");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Something went wrong while fetching data.");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, type]);

  const handleDateRangeFilter = (range) => {
    const today = new Date();
    let start, end;

    switch (range) {
      case "today":
        start = end = toIST(today);
        break;

      case "yesterday":
        const y = new Date(today);
        y.setDate(today.getDate() - 1);
        start = end = toIST(y);
        break;

      case "thisWeek":
        const f = new Date(today);
        f.setDate(today.getDate() - today.getDay());
        const l = new Date(f);
        l.setDate(f.getDate() + 6);
        start = toIST(f);
        end = toIST(l);
        break;

      case "thisMonth":
        const sm = new Date(today.getFullYear(), today.getMonth(), 1);
        const em = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        start = toIST(sm);
        end = toIST(em);
        break;

      case "last6Months":
        const s6 = new Date(today.getFullYear(), today.getMonth() - 5, 1);
        const e6 = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        start = toIST(s6);
        end = toIST(e6);
        break;

      case "thisYear":
        const sy = new Date(today.getFullYear(), 0, 1);
        const ey = new Date(today.getFullYear(), 11, 31);
        start = toIST(sy);
        end = toIST(ey);
        break;

      default:
        start = end = "";
    }

    setSelectedBtn(range);
    setStartDate(start);
    setEndDate(end);
  };

  const handleFormDate = (e) => {
    if (e.target.name === "startDate") {
      setStartDate(e.target.value);
      setSelectedBtn("range");
    } else {
      setEndDate(e.target.value);
      setSelectedBtn("range");
    }
  };

  const filteredPlayData = playData.filter(
    (play) =>
      (filteredGame ? play.game_name === filteredGame : true) &&
      (filteredUser ? play.u_info.includes(filteredUser) : true)
  );

  const uniqueUserPlayed = [
    ...new Set(filteredPlayData.map((play) => play.u_info)),
  ].length;

  const successWithdraw = withdrawData?.filter((w) => w.status === true).length;
  const pendingWithdrawCount = withdrawData?.filter(
    (w) => w.status === false
  ).length;
  return (
    <>
      {loader ? (
        <Loader loader={loader} />
      ) : (
        <div className="w-full md:ml-[4.4rem] px-3 sm:px-4 lg:px-6 mt-16">
          <h1 className="flex justify-start items-center gap-3 text-2xl md:text-4xl font-extrabold text-main-font my-3 tracking-tight">
            <span className="relative p-3 bg-main-blue text-white rounded-2xl shadow-md flex items-center justify-center">
              <IoMdAnalytics className="text-xl md:text-2xl" />

              {/* 🔥 Pending Request Badge */}
              {pendingWithdraw > 0 && (
                <span
                  className="absolute  cursor-pointer -top-1 -right-1 bg-red-600 text-white text-xs md:text-sm font-bold rounded-full px-2 py-0.5 shadow"
                  onClick={() => navigate("/manage/withdraw")}
                >
                  {pendingWithdraw}
                </span>
              )}
            </span>

            <span className="text-main-font">Dashboard Overview</span>
          </h1>

          {/* Date Filter */}
          <section className="bg-white rounded-3xl shadow-lg p-4 md:p-6 border border-gray-100">
            <div className="grid grid-cols-12 gap-4 items-center">
              {/* Desktop: Date Range Buttons */}
              <div className="hidden md:flex col-span-12 md:col-span-8 flex-wrap gap-3">
                {dateRanges.map((range) => (
                  <MainButton
                    key={range}
                    onClick={() => handleDateRangeFilter(range)}
                    label={range
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (s) => s.toUpperCase())}
                    selectState={selectedBtn === range}
                    className="mb-2"
                  />
                ))}
              </div>

              {/* Mobile: Date Range Dropdown */}
              <div className="block md:hidden col-span-12">
                <select
                  value={selectedBtn}
                  onChange={(e) => handleDateRangeFilter(e.target.value)}
                  className="w-full cursor-pointer rounded-lg border border-gray-300 px-4 py-2 text-sm shadow-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Range</option>
                  {dateRanges.map((range) => (
                    <option key={range} value={range}>
                      {range
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (s) => s.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Inputs (Desktop Only) */}
              <form className="hidden md:flex col-span-12 md:col-span-4 gap-2 justify-end mt-2 md:mt-0">
                <input
                  type="date"
                  name="startDate"
                  value={startDate}
                  onChange={handleFormDate}
                  className="cursor-pointer rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="date"
                  name="endDate"
                  value={endDate}
                  onChange={handleFormDate}
                  className="cursor-pointer rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-blue-500"
                />
              </form>
            </div>

            <hr className="my-4 border-1" />
            {/* Mobile: Dropdown */}
            <div className="block md:hidden mt-4">
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full cursor-pointer rounded-lg border border-gray-300 px-4 py-2 text-sm shadow-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All</option>
                <option value="user">User</option>
                <option value="play">Play</option>
                <option value="withdraw">Withdraw</option>
              </select>
            </div>

            {/* Desktop: Buttons */}
            <div className="hidden md:flex flex-wrap gap-3 items-center mt-4">
              {["all", "user", "play", "withdraw"].map((option) => (
                <MainButton
                  key={option}
                  onClick={() => setType(option)}
                  label={option.charAt(0).toUpperCase() + option.slice(1)}
                  selectState={type === option}
                />
              ))}
            </div>
          </section>

          {/* Stat Cards — Large UI */}
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-6">
            {(type === "all" || type === "user") && (
              <div
                onClick={() => navigate("/manage/user/all/data")}
                className="relative bg-gradient-to-br from-purple-600 to-purple-400 rounded-3xl text-white p-6 shadow-xl hover:scale-[1.02] transition-transform cursor-pointer overflow-hidden"
              >
                <div className="absolute top-0 right-0 opacity-10 text-[8rem]">
                  <FaRegUser />
                </div>
                <h3 className="text-lg font-semibold uppercase tracking-wide">
                  New Users
                </h3>
                <p className="text-4xl font-bold mt-3">{userData.length}</p>
                <p className="text-sm mt-2 opacity-80">
                  Registered in selected range
                </p>
              </div>
            )}

            {(type === "all" || type === "play") && (
              <>
                <div className="relative bg-gradient-to-br from-green-600 to-green-400 rounded-3xl text-white p-6 shadow-xl hover:scale-[1.02] transition-transform overflow-hidden">
                  <div className="absolute top-0 right-0 opacity-10 text-[8rem]">
                    <FaGamepad />
                  </div>
                  <h3 className="text-lg font-semibold uppercase tracking-wide">
                    Total Plays
                  </h3>
                  <p className="text-4xl font-bold mt-3">{playData.length}</p>
                  <p className="text-sm mt-2 opacity-80">
                    Games played in selected range
                  </p>
                </div>

                <div className="relative bg-gradient-to-br from-blue-600 to-blue-400 rounded-3xl text-white p-6 shadow-xl hover:scale-[1.02] transition-transform overflow-hidden">
                  <div className="absolute top-0 right-0 opacity-10 text-[8rem]">
                    <FaChartLine />
                  </div>
                  <h3 className="text-lg font-semibold uppercase tracking-wide">
                    Unique Players
                  </h3>
                  <p className="text-4xl font-bold mt-3">{uniqueUserPlayed}</p>
                  <p className="text-sm mt-2 opacity-80">
                    Distinct users who played
                  </p>
                </div>
              </>
            )}

            {(type === "all" || type === "withdraw") && (
              <>
                <div className="relative bg-gradient-to-br from-yellow-600 to-yellow-400 rounded-3xl text-white p-6 shadow-xl hover:scale-[1.02] transition-transform overflow-hidden">
                  <div className="absolute top-0 right-0 opacity-10 text-[8rem]">
                    💸
                  </div>
                  <h3 className="text-lg font-semibold uppercase tracking-wide">
                    Success Withdraw
                  </h3>
                  <p className="text-4xl font-bold mt-3 flex items-center gap-2">
                    ₹
                    {withdrawData
                      .filter((w) => w.status === true)
                      .reduce(
                        (acc, curr) => acc + Number(curr.u_Amount || 0),
                        0
                      )}
                    <span className="text-xl font-semibold opacity-90">
                      ({successWithdraw})
                    </span>
                  </p>
                  <p className="text-sm mt-2 opacity-80">
                    Successful withdrawal
                  </p>
                </div>

                <div
                  className="relative bg-gradient-to-br from-red-600 to-red-400 rounded-3xl text-white p-6 shadow-xl hover:scale-[1.02] transition-transform overflow-hidden cursor-pointer"
                  onClick={() => navigate("/manage/withdraw")}
                >
                  <div className="absolute top-0 right-0 opacity-10 text-[8rem]">
                    🚫
                  </div>
                  <h3 className="text-lg font-semibold uppercase tracking-wide">
                    Pending Withdraw
                  </h3>
                  <p className="text-4xl font-bold mt-3 flex items-center gap-2">
                    ₹
                    {withdrawData
                      .filter((w) => w.status === false)
                      .reduce(
                        (acc, curr) => acc + Number(curr.u_Amount || 0),
                        0
                      )}
                    <span className="text-xl font-semibold opacity-90">
                      ({pendingWithdrawCount})
                    </span>
                  </p>
                  <p className="text-sm mt-2 opacity-80">
                    Waiting for approval
                  </p>
                </div>
              </>
            )}
          </section>

          {/* Charts */}
          {type === "all" ? (
            <section className="mt-6 grid grid-cols-1 lg:grid-cols-1 gap-6">
              <UserChart userData={userData} />
              <section className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <PlayAnalyticsChart playData={filteredPlayData} />
                <WithdrawAnalyticsChart withdrawData={withdrawData} />
              </section>
            </section>
          ) : (
            <>
              {type === "user" && (
                <section className="mt-6">
                  <UserChart userData={userData} />
                </section>
              )}

              {type === "play" && (
                <section className="mt-6">
                  <PlayAnalyticsChart playData={filteredPlayData} />
                </section>
              )}

              {type === "withdraw" && (
                <section className="mt-6">
                  <WithdrawAnalyticsChart withdrawData={withdrawData} />
                </section>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default Home;
